import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { pathByRouteName } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { InlineTextButton, Logo, Heading, LayoutSingleColumn } from '../../components';

import css from './LoadableComponentErrorBoundary.module.css';

/**
 * Fallback page rendered by LoadableComponentErrorBoundary.
 *
 * @param {Object} props
 * @param {'chunk' | 'generic'} [props.variant] - 'chunk' = stale-build ChunkLoadError
 *   ("refresh to update"); 'generic' = any other runtime throw (warm, jargon-free
 *   apology). Defaults to 'chunk' to preserve the original behaviour.
 */
export const LoadableComponentErrorBoundaryPage = ({ variant = 'chunk' }) => {
  const routeConfiguration = useRouteConfiguration();
  const landingPagePath = pathByRouteName('LandingPage', routeConfiguration);
  const handleOnClick = () => {
    if (typeof window !== 'undefined') {
      window.location = landingPagePath;
    }
  };

  const isChunk = variant === 'chunk';
  const headingId = isChunk
    ? 'LoadableComponentErrorBoundaryPage.heading'
    : 'LoadableComponentErrorBoundaryPage.genericHeading';
  const descriptionId = isChunk
    ? 'LoadableComponentErrorBoundaryPage.description'
    : 'LoadableComponentErrorBoundaryPage.genericDescription';

  const landingPageLink = (
    <InlineTextButton onClick={handleOnClick}>
      <FormattedMessage id="LoadableComponentErrorBoundaryPage.landingPageLink" />
    </InlineTextButton>
  );

  return (
    <div>
      <LayoutSingleColumn
        topbar={
          <div className={css.topbar}>
            <InlineTextButton onClick={handleOnClick}>
              <Logo className={css.logoMobile} layout="mobile" />
              <Logo className={css.logoDesktop} layout="desktop" />
            </InlineTextButton>
          </div>
        }
        footer={null}
      >
        <div className={css.root}>
          <div className={css.content}>
            {/* The "404" numeral only makes sense for the stale-chunk case; a
                generic runtime error is not a not-found. */}
            {isChunk ? <div className={css.number}>404</div> : null}
            <Heading as="h1" rootClassName={css.heading}>
              <FormattedMessage id={headingId} />
            </Heading>
            <p className={css.description}>
              <FormattedMessage id={descriptionId} values={{ link: landingPageLink }} />
            </p>
          </div>
        </div>
      </LayoutSingleColumn>
    </div>
  );
};
